import { Dispatch, FC, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { MarketCapCategory, MarketCapData, MarketCapsParamType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { marketCapRadarCategory, setMarketCapRadarCategory } from '../../lib/store/discover/marketCapRadarState';
import { getMarketCaps as getMarketCapsCall } from '../../lib/store/fundamental';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { defaultLogoBase64 } from '../../lib/util/assets/logos';
import {
  isCallStatusInitial,
  isCallStatusPending,
  isCallStatusReady,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import MarketCapRadar from '../../shared_components/MarketCapRadar/MarketCapRadar';
import { MARKET_CAP_RADAR_ROW_COUNT } from '../../shared_components/MarketCapRadar/MarketCapRadar.constants';
import { MarketCapRow } from '../../shared_components/MarketCapRadar/MarketCapRadar.types';
import { UI_ROUTES } from '../../util/routes';

import { shouldFetchMarketCaps } from './marketCapRadar.helpers';

const MarketCapRadarWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getMarketCaps,
    isMarketCapsInitial,
    isMarketCapsReady,
    isMarketCapsPending,
    marketCapsData,
    companyLogoAndName,
  } = props;
  const { responseData, params: { category: loadedCategory } } = marketCapsData;
  const gainers = responseData.gainers.content;
  const losers = responseData.losers.content;
  // Both lists page through the same bucket, so either page carries its paging info.
  const hasMorePages = responseData.gainers.totalPages > 1;
  const hasMoreThanShownRows = hasMorePages
    || gainers.length > MARKET_CAP_RADAR_ROW_COUNT
    || losers.length > MARKET_CAP_RADAR_ROW_COUNT;

  const navigate = useNavigate();
  const selectedCategory = marketCapRadarCategory.use();

  // Single market: the price cache is keyed by the plain symbol and prices are in dollars.
  const toRows = useMemo(() => (items: MarketCapData[]): MarketCapRow[] => (
    items.map(item => ({
      ...item,
      lookupSymbol: item.symbol,
      currencySymbol: CurrencySymbolEnum.USD,
      logo: companyLogoAndName[item.symbol]?.logo ?? defaultLogoBase64,
      logoUrl: item.url ?? companyLogoAndName[item.symbol]?.url ?? '',
      displayName: item.name ?? companyLogoAndName[item.symbol]?.name ?? '',
    }))
  ), [companyLogoAndName]);

  const gainerRows = useMemo(() => toRows(gainers), [toRows, gainers]);
  const loserRows = useMemo(() => toRows(losers), [toRows, losers]);

  // The fetch effect below reacts to the new category - dispatching here as well would fire twice.
  const handleCategoryChange = (category: MarketCapCategory) => {
    setMarketCapRadarCategory(category);
  };

  const symbolsToSubscribe = useMemo(() => {
    const topGainers = gainers.slice(0, MARKET_CAP_RADAR_ROW_COUNT);
    const topLosers = losers.slice(0, MARKET_CAP_RADAR_ROW_COUNT);

    return [...topGainers, ...topLosers].map(item => ({
      symbol: item.symbol,
      stockExchangeCode: item.stockExchangeCode,
      countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
    }));
  }, [gainers, losers, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isMarketCapsReady && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    if (!shouldFetchMarketCaps({ isInitial: isMarketCapsInitial, loadedCategory, selectedCategory })) {
      return;
    }

    getMarketCaps({ category: selectedCategory });
  }, [getMarketCaps, isMarketCapsInitial, loadedCategory, selectedCategory]);

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleViewAllClick = (isGainersSelected: boolean) => {
    navigate(UI_ROUTES.discover + UI_ROUTES.marketCapRadar, { state: { isGainersSelected } });
  };

  return (
    <MarketCapRadar
      gainers={gainerRows}
      losers={loserRows}
      isReady={isMarketCapsReady}
      isPending={isMarketCapsPending}
      hasMoreThanShownRows={hasMoreThanShownRows}
      selectedCategory={selectedCategory}
      onCategoryChange={handleCategoryChange}
      onSymbolClick={handleSymbolClick}
      onViewAllClick={handleViewAllClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isMarketCapsInitial: isCallStatusInitial(FundamentalCall.getMarketCaps, state.fundamental.statusByCall),
  isMarketCapsReady: isCallStatusReady(FundamentalCall.getMarketCaps, state.fundamental.statusByCall),
  isMarketCapsPending: isCallStatusPending(FundamentalCall.getMarketCaps, state.fundamental.statusByCall),
  marketCapsData: state.fundamental.marketCapsData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getMarketCaps: (payload: MarketCapsParamType) => dispatch(getMarketCapsCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MarketCapRadarWrapper);
